/**
 * In-page interaction capture script injected via addInitScript.
 * Captures user interactions to window.__canaryRecordedEvents.
 *
 * @module record-interaction-script
 */

export const INTERACTION_CAPTURE_SCRIPT = `
(function() {
  if (window.__canaryRecordedEvents) return;
  window.__canaryRecordedEvents = [];

  function push(event) {
    window.__canaryRecordedEvents.push(event);
  }

  // --- Input debouncing ---
  // Accumulate input per element, flush after 500ms idle as a single event
  var inputTimers = new Map();
  var inputState = new Map();

  function getElementKey(el) {
    return (el.id || '') + '|' + (el.getAttribute('aria-label') || '') + '|' + (el.tagName || '') + '|' + (el.getAttribute('name') || '');
  }

  function flushInput(key) {
    var state = inputState.get(key);
    if (!state) return;
    inputTimers.delete(key);
    inputState.delete(key);
    push({
      type: 'input',
      ts: state.ts,
      tagName: state.tagName,
      id: state.id || undefined,
      inputType: state.inputType || undefined,
      ariaLabel: state.ariaLabel || undefined,
      value: state.value,
      isFinal: true,
    });
  }

  function flushAllInputs() {
    inputTimers.forEach(function(timer) { clearTimeout(timer); });
    inputState.forEach(function(_, key) { flushInput(key); });
  }

  document.addEventListener('click', function(e) {
    var el = e.target;
    push({
      type: 'click',
      ts: Date.now(),
      x: e.clientX,
      y: e.clientY,
      tagName: el.tagName || '',
      id: el.id || undefined,
      testId: el.getAttribute('data-testid') || undefined,
      ariaLabel: el.getAttribute('aria-label') || undefined,
      role: el.getAttribute('role') || undefined,
      textContent: (el.textContent || '').slice(0, 200).trim() || undefined,
      className: el.className && typeof el.className === 'string' ? el.className.slice(0, 200) : undefined,
    });
  }, true);

  document.addEventListener('input', function(e) {
    var el = e.target;
    var isPassword = el.type === 'password';
    var key = getElementKey(el);

    // Clear previous timer for this element
    var prevTimer = inputTimers.get(key);
    if (prevTimer) clearTimeout(prevTimer);

    // Update accumulated state
    inputState.set(key, {
      ts: Date.now(),
      tagName: el.tagName || '',
      id: el.id || undefined,
      inputType: el.type || undefined,
      ariaLabel: el.getAttribute('aria-label') || undefined,
      value: isPassword ? '***' : (el.value || ''),
    });

    // Schedule flush after 500ms idle
    inputTimers.set(key, setTimeout(function() { flushInput(key); }, 500));
  }, true);

  // --- Change events for selects, checkboxes, radios ---
  document.addEventListener('change', function(e) {
    var el = e.target;
    var tagName = el.tagName || '';
    var inputType = (el.type || '').toLowerCase();

    // Only capture selects, checkboxes, and radios (text inputs are handled by input debouncing)
    var isSelect = tagName === 'SELECT';
    var isCheckOrRadio = inputType === 'checkbox' || inputType === 'radio';
    if (!isSelect && !isCheckOrRadio) return;

    var value;
    if (isCheckOrRadio) {
      value = el.checked ? 'checked' : 'unchecked';
    } else {
      value = el.value || '';
    }

    push({
      type: 'change',
      ts: Date.now(),
      tagName: tagName,
      id: el.id || undefined,
      inputType: inputType || undefined,
      ariaLabel: el.getAttribute('aria-label') || undefined,
      value: value,
    });
  }, true);

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') {
      push({
        type: 'keydown',
        ts: Date.now(),
        key: e.key,
      });
    }
  }, true);

  // --- SPA navigation: patch pushState/replaceState ---
  var origPushState = history.pushState;
  var origReplaceState = history.replaceState;

  history.pushState = function() {
    origPushState.apply(this, arguments);
    push({
      type: 'navigation',
      ts: Date.now(),
      url: window.location.href,
      navigationType: 'pushState',
    });
  };

  history.replaceState = function() {
    origReplaceState.apply(this, arguments);
    push({
      type: 'navigation',
      ts: Date.now(),
      url: window.location.href,
      navigationType: 'replaceState',
    });
  };

  window.addEventListener('popstate', function() {
    push({
      type: 'navigation',
      ts: Date.now(),
      url: window.location.href,
      navigationType: 'popstate',
    });
  });

  window.addEventListener('beforeunload', function() {
    flushAllInputs();
    push({
      type: 'navigation',
      ts: Date.now(),
      url: window.location.href,
      navigationType: 'beforeunload',
    });
  });

  // Exposed for discardCapturedEvents — cancels pending debounce timers
  // without flushing their events, then clears the array.
  window.__canaryFlushAndDiscard = function() {
    inputTimers.forEach(function(timer) { clearTimeout(timer); });
    inputTimers.clear();
    inputState.clear();
    window.__canaryRecordedEvents = [];
  };
})();
`;
